import Link from "next/link";
import type { ReactNode } from "react";

import { Breadcrumb, type NamedCrumb } from "../Breadcrumb";
import { PageActions } from "./PageActions";

/** The fancier ampersand — Baskerville italic's Et-glyph (system serif, no
 *  font loading) for display headings: <>Items <Amp /> Assets.</> */
export function Amp() {
  return <span className="amp">&</span>;
}

export function PageHeading({
  title,
  actions,
  crumb,
  crumbs,
}: {
  title: ReactNode;
  actions?: ReactNode;
  /** The whole breadcrumb, named by the page (see {@link Breadcrumb}). */
  crumbs?: readonly NamedCrumb[];
  /** Verbatim breadcrumb label for the current (last) path segment — e.g. a
   *  SKU slug, so it isn't turned into spaced words. */
  crumb?: string;
}) {
  return (
    <header className="mb-8 border-b border-line pb-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        {/* On a phone the title takes the row and wraps, so the actions'
            button (PageActions) sits beside it rather than under it. */}
        <div className="max-sm:min-w-0 max-sm:flex-1">
          <Breadcrumb currentLabel={crumb} crumbs={crumbs} />
          <h1 className="display text-3xl text-fg sm:text-4xl">{title}</h1>
        </div>
        {/* flex-wrap so a page with several actions (e.g. the agent's new /
            permissions / prompt / settings) wraps onto a second line on a
            narrow desktop window instead of pushing the page into horizontal
            scroll; on a phone they fold into a menu instead. */}
        {actions && <PageActions>{actions}</PageActions>}
      </div>
    </header>
  );
}

/** The design guide's corner-frame marks. Default is the top-right +
 *  bottom-left diagonal pair (`⌝ ⌞`) — the public site's `FramedLink`
 *  affordance, used for navigation. Pass `full` for all four corners
 *  (`⌜ ⌝ ⌞ ⌟`, a complete bracket) — reserved for action controls like
 *  the sign-out button.
 *
 *  Render inside a `relative` box; add `group` to that box and pass
 *  `interactive` so the marks brighten with the control's hover/focus. A
 *  label inside pads one pixel more below than above (`pt-0.75 pb-1.25`, not
 *  `py-1`): the mono face sits low, and this puts the words at the middle of
 *  the frame. */
export function CornerFrame({
  interactive = false,
  full = false,
}: {
  interactive?: boolean;
  full?: boolean;
}) {
  const c = interactive
    ? "absolute leading-none text-fg-subtle transition-colors group-hover:text-fg-muted group-focus-visible:text-fg"
    : "absolute leading-none text-fg-subtle";
  return (
    <>
      {full && (
        <span aria-hidden className={`${c} top-0 left-0`}>
          ⌜
        </span>
      )}
      <span aria-hidden className={`${c} top-0 right-0`}>
        ⌝
      </span>
      <span aria-hidden className={`${c} bottom-0 left-0`}>
        ⌞
      </span>
      {full && (
        <span aria-hidden className={`${c} bottom-0 right-0`}>
          ⌟
        </span>
      )}
    </>
  );
}

/** Navigation link with the design guide's `⌞ ⌝` corner-frame
 *  affordance — visually equivalent to `FramedLink` on the public
 *  site, but bound to `next/link` instead of the i18n-aware Link
 *  (office routes aren't in the locale pathnames map). Reserved for
 *  navigation; pill-style rounded buttons are used for actions
 *  (form submit, in-page interactions). Omit `href` to render the
 *  disabled placeholder — greyed out, inert — for a destination that
 *  is not available right now (a paginator on its first / last page,
 *  an action off a deleted row). */
export function ActionLink({
  href,
  children,
  className = "",
}: {
  href?: string;
  children: ReactNode;
  className?: string;
}) {
  // 3px over 5px, as in FramedLink: the label sits at the middle of its frame.
  const baseClass = `relative inline-block px-3 pt-0.75 pb-1.25 font-mono text-sm ${className}`;
  // The disabled placeholder (no href) is inert, so its frame doesn't
  // react to hover/focus.
  if (!href) {
    return (
      <span
        aria-disabled="true"
        className={`${baseClass} cursor-not-allowed text-fg-subtle`}
      >
        <CornerFrame />
        {children}
      </span>
    );
  }
  return (
    <Link
      href={href}
      scroll={false}
      className={`group ${baseClass} text-fg focus-visible:outline-none focus-visible:no-underline`}
    >
      <CornerFrame interactive />
      {children}
    </Link>
  );
}
